import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DollarSign, Check, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PriceProposalMessageProps {
  message: string;
  senderId: string;
  conversationId: string;
  onUpdate: () => void;
}

export default function PriceProposalMessage({
  message,
  senderId,
  conversationId,
  onUpdate
}: PriceProposalMessageProps) {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversation, setConversation] = useState<any>(null);
  
  // Parse price from message
  const priceMatch = message.match(/\$(\d+(?:\.\d{2})?)/);
  const proposedPrice = priceMatch ? parseFloat(priceMatch[1]) : 0;
  
  // Check if this is the current user's proposal
  const isMyProposal = senderId === user?.id;

  // Load conversation data to check agreement status
  useEffect(() => {
    const loadConversation = async () => {
      const { data } = await supabase
        .from('conversations')
        .select('final_price, price_agreed_by_non_posting_party, price_confirmed_by_provider, participant2_id')
        .eq('id', conversationId)
        .single();
      setConversation(data);
    };
    loadConversation();
  }, [conversationId]);

  // Check if price has already been agreed upon (both parties agreed)
  const isPriceAgreed = conversation?.final_price || 
    (conversation?.price_confirmed_by_provider && conversation?.price_agreed_by_non_posting_party);
  
  // Check if current user is the provider
  const isProvider = user?.id === conversation?.participant2_id;
  
  // Check if provider has confirmed this price
  const providerConfirmed = conversation?.price_confirmed_by_provider;
  
  // Only provider can confirm proposals - clients see Pay button in header once confirmed
  // Don't show confirm buttons if:
  // - It's the current user's own proposal
  // - Price is already agreed
  // - User is not the provider
  // - Provider has already confirmed
  const showConfirmButtons = !isMyProposal && proposedPrice && isProvider && !providerConfirmed && !isPriceAgreed;
  
  if (!showConfirmButtons) {
    let statusText = '';
    if (isPriceAgreed) {
      statusText = ' (Agreement reached)';
    } else if (providerConfirmed) {
      statusText = ' (Provider confirmed - awaiting payment)';
    } else if (!isProvider && !providerConfirmed) {
      statusText = ' (Awaiting provider confirmation)';
    }
    
    return (
      <div className={`border rounded-lg p-3 ${
        isPriceAgreed 
          ? 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800' 
          : providerConfirmed
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
      }`}>
        <div className="flex items-center gap-2">
          <DollarSign className={`h-4 w-4 ${isPriceAgreed ? 'text-gray-600' : providerConfirmed ? 'text-green-600' : 'text-blue-600'}`} />
          <span className={`text-sm font-medium ${
            isPriceAgreed 
              ? 'text-gray-900 dark:text-gray-100' 
              : providerConfirmed
              ? 'text-green-900 dark:text-green-100'
              : 'text-blue-900 dark:text-blue-100'
          }`}>
            {isMyProposal ? "You" : "They"} proposed: ${proposedPrice}
            {statusText}
          </span>
        </div>
      </div>
    );
  }

  const acceptProposal = async () => {
    setIsProcessing(true);
    try {
      // Provider confirms the proposed price
      const { error } = await supabase
        .from('conversations')
        .update({
          price_confirmed_by_provider: true,
          latest_proposal_price: proposedPrice,
          latest_proposal_pricing_type: 'fixed',
        })
        .eq('id', conversationId);

      if (error) throw error;

      // Send confirmation message
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user!.id,
          text: `[PRICE_CONFIRMED] Provider confirmed proposed price: $${proposedPrice}`
        });

      toast.success("Price confirmed");
      onUpdate();
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast.error("Failed to accept proposal");
    } finally {
      setIsProcessing(false);
    }
  };

  const rejectProposal = async () => {
    setIsProcessing(true);
    try {
      // Send rejection message
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user!.id,
          text: `[PRICE_REJECTED] Rejected price proposal of $${proposedPrice}`
        });

      toast.success("Price proposal rejected");
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast.error("Failed to reject proposal");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-yellow-600" />
          <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
            Price proposal: ${proposedPrice}
          </span>
        </div>
        
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={acceptProposal}
            disabled={isProcessing}
            className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Check className="h-3 w-3 mr-1" />
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={rejectProposal}
            disabled={isProcessing}
            className="h-7 px-3 border-red-300 text-red-600 hover:bg-red-50"
          >
            <X className="h-3 w-3 mr-1" />
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}